import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { getHeroScrollSlides } from "@/config/site";
import { useTranslation } from "react-i18next";
import { useHeroScrollProgress, usePrefersReducedMotion } from "../hooks";
import { HomeTwinCtas } from "./home-twin-ctas";

const FILM_GAP_PX = 16;

function slideVisualWeight(i: number, fractional: number) {
  const d = Math.abs(fractional - i);
  const scale = Math.max(0.74, 1 - Math.min(d, 1.35) * 0.2);
  const opacity = Math.max(0.42, 1 - Math.min(d, 1.6) * 0.32);
  const zIndex = Math.max(0, Math.round(20 - d * 8));
  return { scale, opacity, zIndex };
}

/**
 * Hero com scroll pinned: void #000 + vigneta, filme horizontal multi-visível
 * (centro destacado por escala/opacidade), legenda mono sob a galeria.
 */
export function Hero() {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const firstCellRef = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const slides = useMemo(() => getHeroScrollSlides(t), [t, i18n.language]);
  const n = slides.length;
  const { progress, activeIndex } = useHeroScrollProgress({
    sectionRef,
    slideCount: n,
    reducedMotion,
  });

  const [cellWidth, setCellWidth] = useState(300);

  useLayoutEffect(() => {
    const el = firstCellRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setCellWidth(el.offsetWidth);
    });
    ro.observe(el);
    setCellWidth(el.offsetWidth);
    return () => ro.disconnect();
  }, [n, reducedMotion]);

  const fractional = reducedMotion ? 0 : progress * (n - 1);
  const stride = cellWidth + FILM_GAP_PX;
  const translatePx = reducedMotion ? 0 : -progress * (n - 1) * stride;
  const halfCell = cellWidth / 2;

  const active = slides[activeIndex] ?? slides[0];

  return (
    <section
      ref={sectionRef}
      className={
        reducedMotion
          ? "relative z-10 min-h-svh w-full max-[1000px]:min-h-[100vh]"
          : "relative z-10 w-full [min-height:calc(var(--hero-slide-count)*100svh)] [height:calc(var(--hero-slide-count)*100svh)] max-[1000px]:[min-height:calc(var(--hero-slide-count)*100vh)] max-[1000px]:[height:calc(var(--hero-slide-count)*100vh)]"
      }
      style={
        reducedMotion
          ? undefined
          : ({ "--hero-slide-count": String(n) } as CSSProperties)
      }
      aria-label={t("home.heroScroll.ariaLabel")}
    >
      <span className="sr-only" aria-live="polite">
        {reducedMotion
          ? t("home.hero.sub")
          : t("home.heroScroll.srStep", {
              current: activeIndex + 1,
              total: n,
              caption: active.caption,
            })}
      </span>

      <div
        className="sticky top-0 flex h-svh min-h-svh w-full flex-col overflow-hidden border-b border-white/10 bg-hero-void max-[1000px]:h-[100vh] max-[1000px]:min-h-[100vh]"
        aria-hidden={false}
      >
        {/* Base + vigneta roxa suave (marca) */}
        <div className="absolute inset-0 bg-hero-void" aria-hidden />
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_72%_58%_at_50%_42%,transparent_0%,rgb(0_0_0/0.2)_45%,rgb(0_0_0/0.92)_78%,rgb(72_29_115/0.14)_100%)]"
          aria-hidden
        />

        {/* Filme horizontal + legenda tipo referência */}
        <div className="relative z-[2] flex min-h-0 flex-1 flex-col items-center justify-center px-3 pt-14 pb-2 max-[1000px]:pt-16">
          <div className="w-full max-w-[100vw] overflow-hidden">
            {reducedMotion ? (
              <div className="flex justify-center">
                <div
                  ref={firstCellRef}
                  className="flex w-[clamp(140px,28vw,320px)] items-center justify-center max-[1000px]:w-[clamp(120px,34vw,260px)]"
                >
                  <img
                    src={slides[0].src}
                    alt={slides[0].alt}
                    className={`max-h-[min(52vh,520px)] w-full select-none object-contain brightness-[0.88] contrast-[1.05] ${slides[0].blurClass}`}
                    style={{ objectPosition: slides[0].objectPosition }}
                    draggable={false}
                  />
                </div>
              </div>
            ) : (
              <div
                className="flex flex-row items-center gap-4 will-change-transform"
                style={{
                  paddingLeft: `max(0px, calc(50vw - ${halfCell}px))`,
                  paddingRight: `max(0px, calc(50vw - ${halfCell}px))`,
                  transform: `translate3d(${translatePx}px, 0, 0)`,
                }}
              >
                {slides.map((s, i) => {
                  const { scale, opacity, zIndex } = slideVisualWeight(
                    i,
                    fractional,
                  );
                  return (
                    <div
                      key={`${s.src}-${i}`}
                      ref={i === 0 ? firstCellRef : undefined}
                      className="flex w-[clamp(140px,28vw,320px)] shrink-0 items-center justify-center max-[1000px]:w-[clamp(120px,34vw,260px)]"
                    >
                      <div
                        className="relative flex w-full origin-center justify-center"
                        style={{
                          transform: `scale(${scale})`,
                          opacity,
                          zIndex,
                        }}
                      >
                        <img
                          src={s.src}
                          alt={s.alt}
                          className={`max-h-[min(52vh,520px)] w-full select-none object-contain brightness-[0.88] contrast-[1.05] ${s.blurClass}`}
                          style={{ objectPosition: s.objectPosition }}
                          draggable={false}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <p
            key={reducedMotion ? "cap-static" : activeIndex}
            className="mt-5 max-w-xl px-2 text-center font-mono text-[0.7rem] leading-relaxed tracking-[0.04em] text-ink-secondary motion-reduce:transition-none max-[1000px]:mt-4 max-[1000px]:text-[0.65rem]"
          >
            {reducedMotion ? t("home.hero.sub") : active.caption}
          </p>
        </div>

        {/* Headline + CTA */}
        <div className="relative z-[3] w-full shrink-0 px-4 pb-8 text-center max-[1000px]:px-3 max-[1000px]:pb-6">
          <h1 className="font-display text-[clamp(1.35rem,4.2vw,2.75rem)] font-extrabold tracking-tight text-ink">
            {t("home.hero.headline")}
          </h1>
          {!reducedMotion ? (
            <p className="mx-auto mt-3 max-w-2xl text-pretty text-[clamp(0.9rem,2.1vw,1.05rem)] font-normal leading-relaxed text-ink-secondary">
              {t("home.hero.sub")}
            </p>
          ) : null}
          {!reducedMotion ? (
            <p className="mt-2 font-mono text-[0.65rem] uppercase tracking-[0.18em] text-ink-muted max-[1000px]:text-[0.6rem]">
              {t("home.heroScroll.scrollHint")}
            </p>
          ) : null}
          <div className="mx-auto mt-5 w-full max-w-3xl px-1 max-[1000px]:mt-4">
            <HomeTwinCtas />
            <p className="mt-4 font-mono text-[0.65rem] leading-relaxed text-ink-tertiary max-[1000px]:mt-3">
              {t("cta.heroMicro")}
            </p>
          </div>

          {!reducedMotion ? (
            <div
              className="mt-6 flex justify-center gap-2 max-[1000px]:mt-5"
              role="presentation"
              aria-hidden
            >
              {slides.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 w-1 rounded-full transition-colors duration-200 ${
                    i === activeIndex ? "bg-accent" : "bg-ink-muted"
                  }`}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
